import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Server, Globe, Linkedin, Search, Phone, Mail, Bot, Radio, Workflow,
  ScrollText, Settings, Database, BarChart3, Building, ChevronDown,
  Users, UserCheck, Target, Activity, TestTube,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Old Pages dropdown — mirrors the legacy DashboardSidebar grouping
 * (src/components/dashboard/DashboardSidebar.tsx) so muscle memory carries
 * over from the old UX. Sections + nested sub-items render flat with a
 * heading + indentation, since the dropdown isn't tall enough for full
 * collapsibles. Items already in the new-UX primary nav (Dashboard,
 * TAM/CRM, Campaign Performance, Pipeline, Master Inbox, Team, Settings)
 * are still surfaced here as their *old* implementations — e.g.
 * "Master Inbox" links to /old-master-inbox so users can reach the
 * legacy page even though /master-inbox now serves the new design.
 */

type OldPageItem = {
  title: string
  path: string
  icon: LucideIcon
  subItems?: OldPageItem[]
}

type OldPageSection = {
  heading?: string
  items: OldPageItem[]
}

export const sections: OldPageSection[] = [
  {
    items: [
      { title: 'Automation Library', path: '/automation-library', icon: Workflow },
    ],
  },
  {
    heading: 'Internal Sections',
    items: [
      { title: 'Team Management', path: '/team-management', icon: Users },
      { title: 'Clients', path: '/clients', icon: Building },
      {
        title: 'Master CRM',
        path: '/master-crm/accounts',
        icon: UserCheck,
        subItems: [
          { title: 'Accounts', path: '/master-crm/accounts', icon: Building },
          { title: 'Contacts', path: '/master-crm/contacts', icon: Users },
          { title: 'Agencies', path: '/master-crm/agencies', icon: Globe },
          { title: 'TAM Lists', path: '/master-crm/tam', icon: Target },
          { title: 'Signals', path: '/master-crm/signals', icon: Activity },
        ],
      },
      { title: 'Global Settings', path: '/global-settings', icon: Settings },
      { title: 'Logs', path: '/logs', icon: ScrollText },
      {
        title: 'Infrastructure Management',
        path: '/sending-infrastructure',
        icon: Server,
        subItems: [
          { title: 'Domains & Mailboxes', path: '/domains-emails', icon: Globe },
          { title: 'LinkedIn Management', path: '/linkedin-management', icon: Linkedin },
          { title: 'Smartlead Discovery', path: '/smartlead-discovery', icon: Search },
        ],
      },
    ],
  },
  {
    heading: 'Client Sections',
    items: [
      { title: 'Client Dashboard', path: '/comprehensive-dashboard', icon: BarChart3 },
      { title: 'Campaign Reporting', path: '/campaign-performance-test', icon: TestTube },
      { title: 'Master Inbox', path: '/old-master-inbox', icon: Mail },
      { title: 'AI Reply Tagger', path: '/ai-reply-tagger', icon: Bot },
      {
        title: 'Sending Infrastructure',
        path: '/sending-infrastructure',
        icon: Server,
        subItems: [
          { title: 'Cold Calling', path: '/cold-calling', icon: Phone },
          { title: 'Email Activity', path: '/email-activity', icon: Mail },
        ],
      },
      { title: 'Live Feed', path: '/live-feed', icon: Radio },
    ],
  },
  {
    heading: 'Utilities',
    items: [
      { title: 'Backfill', path: '/backfill', icon: Database },
    ],
  },
]

export function OldPagesDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const close = () => setOpen(false)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-3 py-3.5 text-xs font-semibold text-gray-400 hover:text-white transition-colors"
        style={{ letterSpacing: '0.02em' }}
      >
        Old Pages
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-lg z-50 py-1 max-h-[80vh] overflow-y-auto"
          style={{ borderRadius: '12px' }}
        >
          {sections.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              {/* Section divider (skip before first section) */}
              {sectionIdx > 0 && <div className="my-1 border-t border-gray-100" />}
              {section.heading && (
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {section.heading}
                </div>
              )}
              {section.items.map((item) => (
                <React.Fragment key={`${item.title}-${item.path}`}>
                  <Link
                    to={item.path}
                    onClick={close}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <item.icon className="w-4 h-4 text-gray-400" />
                    {item.title}
                  </Link>
                  {item.subItems?.map((sub) => (
                    <Link
                      key={`${item.title}-${sub.path}`}
                      to={sub.path}
                      onClick={close}
                      className="flex items-center gap-2.5 pl-9 pr-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <sub.icon className="w-3.5 h-3.5 text-gray-400" />
                      {sub.title}
                    </Link>
                  ))}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** The same old pages as a plain list, for the profile menu (the header no longer has an Old Pages button). */
export function OldPagesList({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className="max-h-[50vh] overflow-y-auto py-1">
      {sections.map((section, sectionIdx) => (
        <div key={sectionIdx}>
          {section.heading && (
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{section.heading}</div>
          )}
          {section.items.map((item) => (
            <React.Fragment key={`${item.title}-${item.path}`}>
              <Link to={item.path} onClick={onNavigate}
                className="flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors">
                <item.icon className="w-4 h-4 text-gray-400" />
                {item.title}
              </Link>
              {item.subItems?.map((sub) => (
                <Link key={`${item.title}-${sub.path}`} to={sub.path} onClick={onNavigate}
                  className="flex items-center gap-2.5 pl-9 pr-3 py-1 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors">
                  <sub.icon className="w-3.5 h-3.5 text-gray-400" />
                  {sub.title}
                </Link>
              ))}
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  )
}
